import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import type {StreamTab_activity$key} from './__generated__/StreamTab_activity.graphql';
import {normaliseForStream} from './normaliseForStream';
import {ReplyComposer} from './ReplyComposer';
import {StreamHistory} from './StreamHistory';
import type {StreamEntry} from './StreamHistoryCard';

const StreamTabFragment = graphql`
	fragment StreamTab_activity on ActivityEvent @relay(plural: true) {
		__typename
		... on MessageActivity {
			id
			createdAt
			body
			bodyHtml
			authorType
			author {
				fullName
			}
			direction
		}
		... on InternalCommentActivity {
			id
			createdAt
			body
			author {
				fullName
			}
			mentionedUsers {
				id
				fullName
			}
		}
		... on EventActivity {
			id
			createdAt
		}
		... on AttachmentActivity {
			id
			createdAt
		}
	}
`;

interface Props {
	entries: StreamTab_activity$key;
	ticketId: string;
	requesterEmail: string;
	/** Named in the composer's reply hint. */
	requesterName?: string;
	onRefetch: () => void;
}

const Tab = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[500]};
`;
const OrderRow = styled.div`
	display: flex;
	justify-content: flex-end;
`;

type SortDir = 'asc' | 'desc';

/**
 * The conversation, and the box to add to it.
 *
 * Reads oldest-first by default and puts the composer underneath, so the thread runs
 * the way a conversation does and the reply lands where the reading stops. A long
 * thread can be flipped to newest-first, which is the only reason that control
 * exists — it is not a filter and does not change what is shown.
 *
 * The tab is called Stream rather than Comments because it holds emails as well as
 * internal notes.
 */
export function StreamTab({entries, ticketId, requesterEmail, requesterName, onRefetch}: Props) {
	const data = useFragment<StreamTab_activity$key>(StreamTabFragment, entries);
	const [sortDir, setSortDir] = useState<SortDir>('asc');

	const streamEntries = useMemo(() => {
		const result: StreamEntry[] = [];
		for (const entry of data) {
			if (entry.__typename === '%other') continue;
			// Only the turns people took: status changes and attachments belong to the
			// Activity rail, not to the conversation.
			const normalised = normaliseForStream(entry);
			if (normalised) result.push(normalised);
		}
		result.sort((a, b) => {
			const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
			return sortDir === 'asc' ? diff : -diff;
		});
		return result;
	}, [data, sortDir]);

	return (
		<Tab data-testid="stream-tab">
			{streamEntries.length > 1 && (
				<OrderRow>
					<Button
						emphasis={EMPHASIS.LOW}
						onPress={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
						data-cy="stream-sort-toggle"
					>
						{sortDir === 'asc' ? 'Oldest first' : 'Newest first'}
					</Button>
				</OrderRow>
			)}
			<StreamHistory entries={streamEntries} />
			<ReplyComposer
				ticketId={ticketId}
				requesterEmail={requesterEmail}
				requesterName={requesterName}
				onSent={onRefetch}
			/>
		</Tab>
	);
}
